import { useQuery } from "@tanstack/react-query";
import { createFileRoute, redirect } from "@tanstack/react-router";
import { motion } from "framer-motion";
import { useMemo, useState } from "react";

import { BlogFilters, type BlogFilterState } from "@/components/blog/BlogFilters";
import { BlogSummary } from "@/components/blog/BlogSummary";
import { BlogTable } from "@/components/blog/BlogTable";
import { api, type BlogPost } from "@/lib/api";
import { formatRelativeTime } from "@/lib/formatters";
import { isAuthenticated } from "@/lib/pb";

export const Route = createFileRoute("/blog/")({
  beforeLoad: () => {
    if (!isAuthenticated()) throw redirect({ to: "/login" });
  },
  component: BlogPage,
});

const DEFAULT_FILTERS: BlogFilterState = {
  category: "all",
  sort: "recent",
  search: "",
  onlyWithPerf: false,
};

function BlogPage() {
  const [filters, setFilters] = useState<BlogFilterState>(DEFAULT_FILTERS);
  const [forceRefresh, setForceRefresh] = useState(false);

  const query = useQuery({
    queryKey: ["blog", forceRefresh],
    queryFn: () => api.blog.read(forceRefresh),
  });

  const categories = useMemo(() => {
    if (!query.data) return [];
    return Object.keys(query.data.summary.by_category).sort();
  }, [query.data]);

  const filtered = useMemo<BlogPost[]>(() => {
    const posts = query.data?.posts ?? [];
    const search = filters.search.trim().toLowerCase();

    let out = posts.filter((p) => {
      if (filters.category !== "all" && p.category !== filters.category) return false;
      if (filters.onlyWithPerf && !p.perf) return false;
      if (search) {
        const hay = `${p.title_es} ${p.title_en} ${p.slug} ${p.keyword}`.toLowerCase();
        if (!hay.includes(search)) return false;
      }
      return true;
    });

    switch (filters.sort) {
      case "clicks":
        out = out.sort((a, b) => (b.perf?.clicks ?? -1) - (a.perf?.clicks ?? -1));
        break;
      case "impressions":
        out = out.sort((a, b) => (b.perf?.impressions ?? -1) - (a.perf?.impressions ?? -1));
        break;
      case "position":
        out = out.sort((a, b) => (a.perf?.position ?? 999) - (b.perf?.position ?? 999));
        break;
      case "recent":
      default:
        out = out.sort((a, b) => b.published_at.localeCompare(a.published_at));
    }
    return out;
  }, [query.data, filters]);

  if (query.isLoading) return <Shell><Skeleton label="fetching post catalog" /></Shell>;
  if (query.error || !query.data) {
    return (
      <Shell>
        <div className="surface p-6 font-mono text-sm text-danger">
          {(query.error as Error)?.message ?? "no data"}
        </div>
      </Shell>
    );
  }

  const data = query.data;
  const cachedAt = new Date(data.cached_at * 1000).toISOString();

  return (
    <Shell>
      <motion.div
        initial={{ opacity: 0, y: 6 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <p className="path-chip">/blog · post catalog</p>
        <div className="flex items-baseline gap-3 mt-1">
          <h1 className="text-3xl font-semibold">Blog</h1>
          <span className="font-mono text-sm text-muted">
            [{filtered.length}/{data.posts.length}]
          </span>
        </div>
        <p className="mt-1 font-mono text-xs text-muted/60">
          snapshot {formatRelativeTime(cachedAt)}
        </p>
      </motion.div>

      <BlogSummary summary={data.summary} />

      <BlogFilters
        value={filters}
        onChange={setFilters}
        categories={categories}
        onRefresh={() => setForceRefresh((v) => !v)}
        refreshing={query.isFetching}
      />

      <BlogTable posts={filtered} />
    </Shell>
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  return <div className="max-w-6xl mx-auto space-y-4">{children}</div>;
}

function Skeleton({ label }: { label: string }) {
  return (
    <div className="surface p-12 text-center">
      <p className="font-mono text-sm text-muted">$ {label}…</p>
    </div>
  );
}
